import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { finalize } from 'rxjs';

import { Session } from '../../../../services/session';
import { LoadingIndicator } from '../../../../shared/loading-indicator/loading-indicator';
import { Router } from '@angular/router';
import { SchoolService } from '../../../../services/school';
import { ApiEndpoints } from '../../../../core/api-endpoints';
import { ISchool } from '../../../../interfaces/school';
import { FlashService } from '../../../../services/flash';
import { OnboardingService } from '../../../../services/school-onboarding';

@Component({
  selector: 'app-step-one',
  imports: [FormsModule, LoadingIndicator],
  templateUrl: './step-one.html',
  styleUrl: './step-one.css',
})
export class StepOne implements OnInit {
  constructor(
    private readonly schoolService: SchoolService,
    private readonly session: Session,
    private readonly router: Router,
  ) {
    const token = sessionStorage.getItem('token');

    if (!token) {
      void this.router.navigate(['/']);
      return;
    }

    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (!this.schoolId) {
      void this.router.navigate(['/']);
    }
  }

  ngOnInit(): void {
    if (!this.schoolId) {
      return;
    }

    this.getSchoolById(this.schoolId);
  }

  private readonly flashService = inject(FlashService);
  private readonly onboardingService = inject(OnboardingService);
  protected schoolName = signal('');
  protected location = signal('');
  protected school = signal<ISchool | null>(null);
  protected primaryColor = '#003C54';
  protected secondaryColor = '#F2B410';
  protected phone = '';
  protected schoolLogoImage?: File;
  protected imagePreview?: string;
  protected isSubmitting = signal(false);
  protected isLoading = signal(false);

  protected schoolId?: string;

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    const file = input.files?.[0];

    if (!file) {
      return;
    }

    if (this.imagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreview);
    }

    this.schoolLogoImage = file;
    this.imagePreview = URL.createObjectURL(file);
  }

  protected getSchoolById(id: string): void {
    this.isLoading.set(true);

    this.schoolService
      .getSchoolById(`${ApiEndpoints.url}/school/${id}`)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe({
        next: (data) => {
          this.school.set(data);

          this.schoolName.set(data.payload.school_name);

          this.location.set(data.payload.location);

          this.primaryColor = data.payload.primary_color ?? '#003C54';

          this.secondaryColor = data.payload.secondary_color ?? '#F2B410';

          this.phone = data.payload.phone ?? '';

          this.imagePreview = data.payload.school_img ?? undefined;
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Unable to load school data. Refresh the page.');
        },
      });
  }

  protected createSchoolProfile(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields.');

      form.control.markAllAsTouched();
      return;
    }

    if (!this.schoolId) {
      void this.router.navigate(['/']);
      return;
    }

    if (this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    const schoolForm = new FormData();

    schoolForm.append('primary_color', this.primaryColor);

    schoolForm.append('secondary_color', this.secondaryColor);

    schoolForm.append('phone', this.phone);

    if (this.schoolLogoImage) {
      schoolForm.append('school_img', this.schoolLogoImage);
    }

    this.schoolService
      .updateSchoolBrand(`${ApiEndpoints.url}/school/${this.schoolId}/edit`, schoolForm)
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.onboardingService.setSchoolProfileCompleted();

          this.flashService.success('Successfully updated school profile.');
          void this.router.navigate(['/school-onboarding/academic-year-configuration']);
        },
        error: (err) => {
          this.flashService.failure(err?.error?.message ?? 'Failed to update school profile. Try again.');
        },
      });
  }
}
